import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import dayjs from "dayjs";

import ProjectCard from "../cards/ProjectCard";
import Pagination from "../elements/Pagination";

import axios from "axios";
import { ReactComponent as LeftArrow } from "../../resources/icons/left_arrow.svg";

const AdminAssignorProjects: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const assignorName = location.state?.name ?? "Assignor";

  const [projects, setProjects] = useState<any[]>([]);
  const [statusCounts, setStatusCounts] = useState({
    all: 0,
    draft: 0,
    open: 0,
    ongoing: 0,
    cancelled: 0,
    completed: 0,
  });

  const [statusFilter, setStatusFilter] = useState("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 6;
  const [totalPages, setTotalPages] = useState(1);

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  useEffect(() => {
    const fetchAssignorProjects = async () => {
      const token = localStorage.getItem("token");
      if (!id || !token) return;

      const queryParams = new URLSearchParams();
      queryParams.append("page", currentPage.toString());
      queryParams.append("limit", rowsPerPage.toString());
      if (statusFilter !== "ALL") {
        queryParams.append("status", statusFilter.toLowerCase());
      }

      try {
        const res = await axios.get(
          `${
            process.env.REACT_APP_URL
          }/admin/assignors/${id}/projects?${queryParams.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "X-Client-Type": "web",
            },
          }
        );

        setProjects(res.data.projects);
        setStatusCounts({
          all: res.data.statusesCount.all || 0,
          draft: res.data.statusesCount.draft || 0,
          open: res.data.statusesCount.open || 0,
          ongoing: res.data.statusesCount.ongoing || 0,
          cancelled: res.data.statusesCount.cancelled || 0,
          completed: res.data.statusesCount.completed || 0,
        });
        setTotalPages(res.data.totalPages || 1);
      } catch {}
    };

    fetchAssignorProjects();
  }, [id, currentPage, statusFilter]);

  const statusButtons = [
    { label: "ALL", count: statusCounts.all },
    { label: "DRAFT", count: statusCounts.draft },
    { label: "OPEN", count: statusCounts.open },
    { label: "ONGOING", count: statusCounts.ongoing },
    { label: "CANCELLED", count: statusCounts.cancelled },
    { label: "COMPLETED", count: statusCounts.completed },
  ];

  return (
    <div className="flex">
      <div className="flex-1 p-6 ml-[17.5rem]">
        <div className="mb-6 flex justify-between items-center">
          <div className="flex flex-row items-baseline">
            <button
              onClick={() => navigate("/admin/assignors")}
              className="flex items-center font-bold mb-4 mr-3"
            >
              <LeftArrow className="w-6 h-6" style={{ color: "#344054" }} />
            </button>
            <h1
              className="text-2xl font-bold truncate max-w-[22rem]"
              title={assignorName}
            >
              {assignorName} projects
            </h1>
            <p className="text-sm text-[#344054] bg-[#F9FAFB] border border-[#EAECF0] px-2 py-[2px] rounded-full whitespace-nowrap ml-3">
              Assignor
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-6">
          {statusButtons.map(({ label, count }) => (
            <button
              key={label}
              onClick={() => handleStatusFilter(label)}
              className={`px-4 py-2 rounded-full flex items-center gap-2 ${
                statusFilter === label ? "bg-[#FEF0E8]" : "bg-[#F4F4F4]"
              }`}
            >
              <span
                className={`font-semibold text-sm ${
                  statusFilter === label ? "text-orange-500 font-bold" : ""
                }`}
              >
                {label.charAt(0) + label.slice(1).toLowerCase()}
              </span>
              <span className="bg-[#F9FAFB] border border-[#EAECF0] px-2 py-0.5 text-sm rounded-full">
                {count}
              </span>
            </button>
          ))}
        </div>

        {projects.length > 0 ? (
          <div className="max-w-screen grid xl:grid-cols-3 lg:grid-cols-2 grid-cols-1 gap-4">
            {projects.map((project) => (
              <ProjectCard
                key={project.id}
                id={project.id}
                title={project.title}
                publishedAt={
                  project.publishedAt
                    ? dayjs(project.publishedAt).format("DD.MM.YYYY")
                    : dayjs(project.createdAt).format("DD.MM.YYYY")
                }
                status={
                  project.status.charAt(0).toUpperCase() +
                  project.status.slice(1).toLowerCase()
                }
                summary={project.summary || "No summary available."}
              />
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500 text-sm py-12">
            No projects found.
          </div>
        )}

        {totalPages > 1 && (
          <div className="border-t mt-4 pt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAssignorProjects;
